import { FetchAnalysis } from "api";
import { StudentIDState } from "global-states";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { AnalysisPayload } from "./types";

export default function useAnalysis() {
  const studentID = useRecoilValue(StudentIDState);

  const [data, setData] = useState<AnalysisPayload | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (studentID === "") return;
    setIsLoading(true);
    FetchAnalysis(studentID)
      .then((res) => {
        setError(undefined);
        setData(transformData(res.data));
      })
      .catch((err) => setError(JSON.stringify(err)))
      .finally(() => setIsLoading(false));
  }, [studentID]);
  return { data, error, isLoading };
}

function transformData(data: AnalysisPayload): AnalysisPayload {
  return {
    data: {
      pred: {
        all: {
          最高: transformNegativeValueToNaN(data.data.pred.all?.最高),
          最低: transformNegativeValueToNaN(data.data.pred.all?.最低),
          中位數: transformNegativeValueToNaN(data.data.pred.all?.中位數),
          平均: transformNegativeValueToNaN(data.data.pred.all?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.pred.all?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.pred.all?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.pred.all?.學生),
        },
        cat_N: {
          最高: transformNegativeValueToNaN(data.data.pred.cat_N?.最高),
          最低: transformNegativeValueToNaN(data.data.pred.cat_N?.最低),
          中位數: transformNegativeValueToNaN(data.data.pred.cat_N?.中位數),
          平均: transformNegativeValueToNaN(data.data.pred.cat_N?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.pred.cat_N?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.pred.cat_N?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.pred.cat_N?.學生),
        },
        cat_M: {
          最高: transformNegativeValueToNaN(data.data.pred.cat_M?.最高),
          最低: transformNegativeValueToNaN(data.data.pred.cat_M?.最低),
          中位數: transformNegativeValueToNaN(data.data.pred.cat_M?.中位數),
          平均: transformNegativeValueToNaN(data.data.pred.cat_M?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.pred.cat_M?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.pred.cat_M?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.pred.cat_M?.學生),
        },
        cat_S: {
          最高: transformNegativeValueToNaN(data.data.pred.cat_S?.最高),
          最低: transformNegativeValueToNaN(data.data.pred.cat_S?.最低),
          中位數: transformNegativeValueToNaN(data.data.pred.cat_S?.中位數),
          平均: transformNegativeValueToNaN(data.data.pred.cat_S?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.pred.cat_S?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.pred.cat_S?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.pred.cat_S?.學生),
        },
        cat_A: {
          最高: transformNegativeValueToNaN(data.data.pred.cat_A?.最高),
          最低: transformNegativeValueToNaN(data.data.pred.cat_A?.最低),
          中位數: transformNegativeValueToNaN(data.data.pred.cat_A?.中位數),
          平均: transformNegativeValueToNaN(data.data.pred.cat_A?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.pred.cat_A?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.pred.cat_A?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.pred.cat_A?.學生),
        },
        cat_D: {
          最高: transformNegativeValueToNaN(data.data.pred.cat_D?.最高),
          最低: transformNegativeValueToNaN(data.data.pred.cat_D?.最低),
          中位數: transformNegativeValueToNaN(data.data.pred.cat_D?.中位數),
          平均: transformNegativeValueToNaN(data.data.pred.cat_D?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.pred.cat_D?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.pred.cat_D?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.pred.cat_D?.學生),
        },
        user: {
          平均表現: transformNegativeValueToNaN(
            data.data.pred.user?.平均表現 || {}
          ),
          數: transformNegativeValueToNaN(data.data.pred.user?.數 || {}),
          度量: transformNegativeValueToNaN(data.data.pred.user?.度量 || {}),
          圖形與空間: transformNegativeValueToNaN(
            data.data.pred.user?.圖形與空間 || {}
          ),
          代數: transformNegativeValueToNaN(data.data.pred.user?.代數 || {}),
          數據處理: transformNegativeValueToNaN(
            data.data.pred.user?.數據處理 || {}
          ),
        },
      },
      results: {
        all: {
          最高: transformNegativeValueToNaN(data.data.results.all?.最高),
          最低: transformNegativeValueToNaN(data.data.results.all?.最低),
          中位數: transformNegativeValueToNaN(data.data.results.all?.中位數),
          平均: transformNegativeValueToNaN(data.data.results.all?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.results.all?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.results.all?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.results.all?.學生),
        },
        cat_N: {
          最高: transformNegativeValueToNaN(data.data.results.cat_N?.最高),
          最低: transformNegativeValueToNaN(data.data.results.cat_N?.最低),
          中位數: transformNegativeValueToNaN(data.data.results.cat_N?.中位數),
          平均: transformNegativeValueToNaN(data.data.results.cat_N?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.results.cat_N?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.results.cat_N?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.results.cat_N?.學生),
        },
        cat_M: {
          最高: transformNegativeValueToNaN(data.data.results.cat_M?.最高),
          最低: transformNegativeValueToNaN(data.data.results.cat_M?.最低),
          中位數: transformNegativeValueToNaN(data.data.results.cat_M?.中位數),
          平均: transformNegativeValueToNaN(data.data.results.cat_M?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.results.cat_M?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.results.cat_M?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.results.cat_M?.學生),
        },
        cat_S: {
          最高: transformNegativeValueToNaN(data.data.results.cat_S?.最高),
          最低: transformNegativeValueToNaN(data.data.results.cat_S?.最低),
          中位數: transformNegativeValueToNaN(data.data.results.cat_S?.中位數),
          平均: transformNegativeValueToNaN(data.data.results.cat_S?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.results.cat_S?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.results.cat_S?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.results.cat_S?.學生),
        },
        cat_A: {
          最高: transformNegativeValueToNaN(data.data.results.cat_A?.最高),
          最低: transformNegativeValueToNaN(data.data.results.cat_A?.最低),
          中位數: transformNegativeValueToNaN(data.data.results.cat_A?.中位數),
          平均: transformNegativeValueToNaN(data.data.results.cat_A?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.results.cat_A?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.results.cat_A?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.results.cat_A?.學生),
        },
        cat_D: {
          最高: transformNegativeValueToNaN(data.data.results.cat_D?.最高),
          最低: transformNegativeValueToNaN(data.data.results.cat_D?.最低),
          中位數: transformNegativeValueToNaN(data.data.results.cat_D?.中位數),
          平均: transformNegativeValueToNaN(data.data.results.cat_D?.平均),
          下四分位: transformNegativeValueToNaN(
            data.data.results.cat_D?.["下四分位"]
          ),
          上四分位: transformNegativeValueToNaN(
            data.data.results.cat_D?.["上四分位"]
          ),
          學生: transformNegativeValueToNaN(data.data.results.cat_D?.學生),
        },
        user: {
          平均表現: transformNegativeValueToNaN(
            data.data.results.user?.平均表現 || {}
          ),
          數: transformNegativeValueToNaN(data.data.results.user?.數 || {}),
          度量: transformNegativeValueToNaN(data.data.results.user?.度量 || {}),
          圖形與空間: transformNegativeValueToNaN(
            data.data.results.user?.圖形與空間 || {}
          ),
          代數: transformNegativeValueToNaN(data.data.results.user?.代數 || {}),
          數據處理: transformNegativeValueToNaN(
            data.data.results.user?.數據處理 || {}
          ),
        },
      },
    },
  };
}

function transformNegativeValueToNaN(
  entry: { [dateKey: string]: number } | undefined
): {
  [dateKey: string]: number;
} {
  if (!entry) return {};
  return Object.entries(entry).reduce<{ [dateKey: string]: number }>(
    (prev, [curKey, curEntry]) => {
      return {
        ...prev,
        [curKey]: curEntry === -1 ? NaN : curEntry,
      };
    },
    {}
  );
}
